import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  getIsRecording,
  getIsSummarizing,
  getRecordingCorrelationId,
} from '~/store/duck/audioRecorder'
import { getAppointmentId } from '~/store/reducers/soap'

/**
 * Manages correlationId locking during active recording states.
 * Uses Redux recordingCorrelationId when set (survives navigation); otherwise
 * falls back to local state locked from currentAppointmentId when recording starts.
 *
 * @returns Locked correlationId if active, null otherwise
 */
export const useCorrelationIdLock = (): string | null => {
  const currentAppointmentId = useSelector(getAppointmentId)
  const isRecording = useSelector(getIsRecording)
  const isSummarizing = useSelector(getIsSummarizing)
  const recordingCorrelationId = useSelector(getRecordingCorrelationId)

  // Fallback: track appointmentId in local state when recording starts (before Redux is set)
  const [localLockedId, setLocalLockedId] = useState<string | null>(null)

  // Fallback: lock from currentAppointmentId when recording starts
  useEffect(() => {
    if (isRecording && currentAppointmentId && !localLockedId) {
      setLocalLockedId(currentAppointmentId)
    }
  }, [isRecording, currentAppointmentId, localLockedId])

  // Unlock when session ends
  useEffect(() => {
    if (!isRecording && !isSummarizing && localLockedId) {
      setLocalLockedId(null)
    }
  }, [isRecording, isSummarizing, localLockedId])

  const isSessionActive = isRecording || isSummarizing
  // Prefer recordingCorrelationId when set — keeps session stable during recording
  // and summarization. Cleared after successful summarization (same SOAP) or
  // when the user explicitly switches sessions (handleSessionChanged / modal switch).
  if (recordingCorrelationId) return recordingCorrelationId
  if (!isSessionActive) return currentAppointmentId ?? null
  return localLockedId
}
